import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Megaphone } from "lucide-react";
import { api, fileUrl, formatError } from "@/lib/api";
import { encodeWav24, encodeMp3, resample, sliceBuffer } from "@/lib/mastering";
import { MasteringStudio } from "./MasteringStudio";

export const AdminMastering = () => {
  const [releases, setReleases] = useState([]);
  const [target, setTarget] = useState("");
  const [source, setSource] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/releases").then((r) => setReleases(r.data)).catch(() => {}); }, []);

  const onCatalog = async (v) => {
    const [rid, tid] = v.split("|");
    const t = releases.find((r) => r.id === rid)?.tracks.find((x) => x.id === tid);
    setTarget(v);
    if (!t?.audio_url) return;
    const res = await fetch(fileUrl(t.audio_url));
    setSource({ arrayBuffer: await res.arrayBuffer(), name: t.title });
  };
  const saveToTrack = async (result, name) => {
    const [rid, tid] = target.split("|");
    const rel = releases.find((r) => r.id === rid);
    if (!rel) return toast.error("Scegli il brano a cui assegnare il master");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([encodeWav24(result.buffer)], `${name || "master"}-master.wav`, { type: "audio/wav" }));
      const { data } = await api.post("/admin/upload", fd);
      await api.put(`/admin/releases/${rid}`, { ...rel, tracks: rel.tracks.map((t) => (t.id === tid ? { ...t, audio_url: data.url } : t)) });
      toast.success("Master assegnato al brano!");
      api.get("/admin/releases").then((r) => setReleases(r.data));
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const publishExample = async (result, name, src) => {
    setBusy(true);
    try {
      const dur = Math.min(45, src.duration);
      const start = Math.max(0, Math.min(src.duration * 0.35, src.duration - dur));
      const up = async (buf, label) => {
        const fd = new FormData();
        fd.append("file", new File([await encodeMp3(await resample(sliceBuffer(buf, start, dur), 44100))], `esempio-${label}.mp3`, { type: "audio/mpeg" }));
        return (await api.post("/admin/upload", fd)).data.url;
      };
      const before_url = await up(src, "originale");
      const after_url = await up(result.buffer, "master");
      const { data: cfg } = await api.get("/admin/services");
      await api.put("/admin/services", { ...cfg, example: { title: name || "Esempio", before_url, after_url } });
      toast.success("Esempio Prima/Dopo pubblicato nella pagina Servizi!");
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-mastering">
      <div>
        <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Studio di mastering <span className="ml-2 align-middle rounded-full bg-emerald-500 text-[#151827] px-3 py-1 text-xs font-bold" data-testid="mastering-free-badge">Gratis per te</span></h2>
        <p className="text-sm text-zinc-400 mt-2 max-w-3xl">Catena da studio: EQ correttivo, compressore multibanda, bassi mono, saturazione analogica, limiter true peak e loudness automatica. 1. Carica un brano · 2. Scegli modalità e genere (o un brano di riferimento) · 3. Confronta A/B · 4. Avvia il mastering ed esporta WAV o MP3.</p>
      </div>
      <select value={target} onChange={(e) => onCatalog(e.target.value)} className="w-full rounded-full bg-[#262b42] border border-white/15 px-4 text-sm h-[52px]" data-testid="mastering-catalog-select">
        <option value="">Scegli un brano del catalogo… (oppure carica un file qui sotto)</option>
        {releases.flatMap((r) => r.tracks.filter((t) => t.audio_url).map((t) => <option key={t.id} value={`${r.id}|${t.id}`}>{r.title} — {t.title}</option>))}
      </select>
      <MasteringStudio source={source} actions={(result, name, src) => (
        <div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => saveToTrack(result, name)} disabled={!target || busy} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold border border-emerald-400 text-emerald-300 disabled:opacity-50" data-testid="mastering-save-btn"><Save className="h-4 w-4" /> Assegna al brano del catalogo</button>
          <button onClick={() => publishExample(result, name, src)} disabled={busy} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold border border-blue-400 text-blue-300 disabled:opacity-50" data-testid="mastering-use-example-btn"><Megaphone className="h-4 w-4" /> Usa come esempio Prima/Dopo nei Servizi</button>
        </div>
          {!target && <p className="text-xs text-zinc-400 mt-2">Per assegnarlo a un brano, sceglilo dal menu del catalogo in alto.</p>}
        </div>
      )} />
    </div>
  );
};
